import Button from "../components/Misc/Button";
import LeftRightContentText from "../components/LeftRightContentText";
import { useAppContext } from "../_context";
import LeaveFeedback from "../components/LeaveFeedback";
import HowItWorks from "../components/HowItWorks/HowItWorks";
import ImageLeftFooter from "../components/Footer/ImageLeftFooter";
import CoachesStringersHero from "../components/Pages/CoachesStringersHero";
import InfoBallSection from "../components/Misc/InfoBallSection";
import People from "../components/Icons/people.svg";
import Pencil from "../components/Icons/pencil.svg";
import Card from "../components/Icons/card.svg";
import Star from "../components/Icons/star.svg";
import Graph from "../components/Icons/graph.svg";
import BlueBoxImageAndText from "../components/Misc/BlueBoxImageAndText";
import StyledBulletList from "../components/StyledBulletList";
import Stats from "../components/Stats/Stats";
import Testimonials from "../components/Testimonials";
import { useEffect, useState } from "react";
import Contentful from "../utilities/Contentful";
import { getLinkToContentfulMedia, getPercent } from "../utilities/Helpers";
import Head from "next/head";

const Coaches = () => {
  const [{ tx, locale }] = useAppContext();
  const [stats, setStats] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  const howItWorks = () => {
    return [
      tx["Create your coach account in minutes."],
      tx[
        "Complete your profile with information about the services you offer."
      ],
      tx[
        "Players will contact you and request a callback via the app (we’ll email you too)."
      ],
      tx[
        "Respond to players and organise lessons. Job done and it’s all free."
      ],
    ];
  };

  const items = [
    {
      icon: People,
      label: tx["Accessible to thousands of active players"],
    },
    {
      icon: Pencil,
      label: tx["Sign up now for free"],
    },
    {
      icon: Card,
      label: tx["No commission"],
    },
    {
      icon: Star,
      label: tx["Boost your profile with ratings and reviews"],
    },
    {
      icon: Graph,
      label: tx["Find clients and boost your earnings"],
    },
  ];

  useEffect(() => {
    async function getStats() {
      const results = await new Contentful().fetchValueOnly({
        content_type: "statistics",
        "fields.slug[in]": "players,coaches,coaching-lessons",
      });
      const intl = new Intl.NumberFormat(locale);

      setStats([
        {
          svg: "/img/stat/stat-1.svg",
          subheading: tx["nPlayers"],
          label:
            intl.format(results.find((x) => x.slug === "players").value) + "+",
        },
        {
          svg: "/img/stat/stat-lesson.svg",
          subheading: tx["Coaching lessons"],
          label:
            intl.format(
              results.find((x) => x.slug === "coaching-lessons").value,
            ) + "+",
        },
        {
          svg: "/img/stat/stat-3.svg",
          subheading: tx["nCoaches"],
          label:
            intl.format(results.find((x) => x.slug === "coaches").value) + "+",
        },
        {
          svg: "/img/stat/stat-engaged.svg",
          subheading: tx["Happy Coaches"],
          label: getPercent(100, locale),
        },
      ]);
    }

    async function getTestimonials() {
      const results = await new Contentful().fetchValueOnly({
        content_type: "testimonial",
        "fields.page": "Coaches",
      });

      setTestimonials(
        results.map((item) => {
          return {
            avatar_url: getLinkToContentfulMedia(item, "avatar"),
            name: item.name,
            role: item.role,
            description: item.testimonial,
          };
        }),
      );
    }

    getStats();
    getTestimonials();
  }, []);

  const bulletList = [
    tx["Reach an engaged community of active players in your area"],
    tx["Boost the demand for your lessons"],
    tx["Build a strong reputation with local players"],
    tx["Find new clients in minutes"],
    tx["Organise group lessons through the app (coming soon)"],
    tx["Manage all payments online (coming soon)"],
  ];

  return (
    <>
      <Head>
        <title>RacketPal - {tx["Coaches"]}</title>
      </Head>
      <CoachesStringersHero as="coaches" />
      <LeftRightContentText title={tx["Our players need you"]}>
        <p>
          {
            tx[
              "Our community of racket sport players is growing by the second, and many of them are looking for experienced coaches or sparring partners to help them improve their game."
            ]
          }
        </p>
        <p className="mb-0">
          {
            tx[
              "Sign up to RacketPal and you’ll instantly be accessible to a network of tens of thousands of players, for free."
            ]
          }
        </p>
      </LeftRightContentText>
      <InfoBallSection items={items} />
      <BlueBoxImageAndText image="/img/coaches/features.jpg">
        <h2>{tx["Why join RacketPal as a coach?"]}</h2>
        <h4 className="text-grey-1 fw-normal">
          {
            tx[
              "With one of the fastest growing sporting communities, it’s an exciting time to join RacketPal. Not to mention, a smart move for your coaching business."
            ]
          }
        </h4>
        <StyledBulletList bulletList={bulletList} />
      </BlueBoxImageAndText>
      <Stats
        items={stats}
        backgroundSplitColor="dark-blue"
        className="section-margin-top"
      />
      <HowItWorks items={howItWorks()}>
        <h2>{tx["How it works"]}</h2>
        <p className="text-grey-1">
          {
            tx[
              "Sign up now for free and you can join the team of RacketPal coaches within minutes. Ready to join? Here’s how it works."
            ]
          }
        </p>
      </HowItWorks>
      <Testimonials
        className="section-padding-top"
        carouselItems={testimonials}
        title={tx["A few words from our coaches"]}
        subtitle={
          tx[
            "Hear why other coaches love using RacketPal to connect with local players looking for their help."
          ]
        }
      />
      <LeaveFeedback />
      <ImageLeftFooter image="/img/footers/coaches-signup.jpg">
        <h2>{tx["Sign up now for FREE"]}</h2>
        <p>
          {
            tx[
              "We don’t charge coaches to join RacketPal. You’ll be accessible to the whole community of local players on RacketPal for free. No commission. No monthly or annual fee."
            ]
          }
        </p>
        <Button href="/coaches/signup">{tx["Get started"]}</Button>
      </ImageLeftFooter>
    </>
  );
};

export default Coaches;
